// @flow

import type { Node } from "react";
import React from "react";

import type { ItemRenderer, PageImage, PageItem } from "@/utils/JsonPageRenderer/types";

/**
 * Transforms each {@link PageItem} and it's children into a {@link Node}.
 */
class JsonPageRenderer {
    /**
     * The registered page item renderers.
     * @type {ItemRenderer<PageItem>[]}
     */
    static renderers: Array<ItemRenderer<PageItem>> = [];
    
    /**
     * Register a page item renderer.
     * @param renderer
     */
    static register(renderer: ItemRenderer<PageItem>) {
        this.renderers.push(renderer);
    }
    
    /**
     * The page items to transform.
     *
     * @type {PageItem[]}
     */
    items: Array<PageItem>;
    
    /**
     * The image references that can be used while rendering a page item.
     *
     * @type {PageImage[]}
     */
    images: Array<PageImage>;
    
    constructor(items: Array<PageItem>, images: Array<PageImage>) {
        this.items = items;
        this.images = images;
    }
    
    /**
     * Lookup an image by it's relative path, returns an empty string if not found.
     *
     * @param key
     * @return {string}
     */
    findImageSource(key: string): ImageSharp | string | null {
        const src = this.images.find(image => image.relativePath === key);
        if (src === undefined) {
            return null;
        }
        
        if (src.childImageSharp) {
            return src.childImageSharp;
        }
        
        if (typeof src.publicURL === "string") {
            return src.publicURL;
        }
        
        return null;
    }
    
    /**
     * Transforms a page item into a node.
     *
     * @param item
     * @return {JSX.Element|*}
     */
    renderItem(item: PageItem): Node {
        // Find the renderer that can handle this items type.
        const renderer = JsonPageRenderer.renderers.find(
            renderer => renderer.match(item) != null,
        );
        
        // Return a fragment if no renderer was found.
        if (renderer === undefined) {
            return <></>;
        }
        
        // Let the renderer do the transformation.
        return renderer.render(this, item, this.renderItems.bind(this));
    }
    
    /**
     * Renders a list of page items.
     *
     * @param items
     * @return {JSX.Element|Array<boolean|number|string|React$Element<*>|React$Portal|Iterable|null>}
     */
    renderItems: ((items?: Array<PageItem>) => Node) = (items?: Array<PageItem>): Node => {
        // Return a fragment if items is not an array.
        if (!Array.isArray(items)) {
            return <></>;
        }
        
        // Map each item into a node.
        return items.map(item => this.renderItem(item));
    };
    
    /**
     * Renders the page.
     *
     * @return {Node}
     */
    render(): Node {
        return this.renderItems(this.items);
    }
}

export default JsonPageRenderer;
